import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Search, 
  UserPlus, 
  Calendar, 
  Brain, 
  Settings, 
  CreditCard,
  BookOpen,
  FileQuestion,
  ShieldCheck,
  Phone,
  ArrowRight,
  HelpCircle
} from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';

const HelpCenter = () => {
  const quickLinks = [
    {
      icon: Search,
      title: "Getting Started",
      description: "Learn how to find and connect with tutors",
      link: "/faqs"
    },
    {
      icon: UserPlus,
      title: "Become a Tutor",
      description: "Start your journey as a verified tutor",
      link: "/tutor/register"
    },
    {
      icon: Calendar,
      title: "Booking Demo Classes",
      description: "How to book and manage demo sessions",
      link: "/faqs"
    },
    {
      icon: Brain,
      title: "Free Quiz Tests",
      description: "Access free quizzes for all subjects",
      link: "/faqs"
    },
    {
      icon: Settings,
      title: "Account Management",
      description: "Manage your profile and settings",
      link: "/faqs"
    },
    {
      icon: CreditCard,
      title: "Payment & Refunds",
      description: "Payment methods and refund policies",
      link: "/faqs"
    }
  ];

  const popularArticles = [
    {
      icon: BookOpen,
      title: "How to find and book a tutor",
      description: "Step-by-step guide to search, compare, and book the perfect tutor for your needs."
    },
    {
      icon: Brain,
      title: "How to take free quizzes",
      description: "Learn how to access our free quiz-based tests and track your progress."
    },
    {
      icon: UserPlus,
      title: "How to register as a tutor",
      description: "Complete guide to becoming a verified tutor on PriEdu platform."
    },
    {
      icon: ShieldCheck,
      title: "Verification process for tutors",
      description: "Understand our rigorous verification process ensuring quality and safety."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-teal-50">
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-600 to-teal-500 rounded-2xl mb-6 shadow-lg">
              <HelpCircle className="h-10 w-10 text-white" />
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              How can we help you?
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Find answers to common questions or get in touch with our support team
            </p>
          </div>

          <div className="mb-20">
            <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">Quick Links</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {quickLinks.map((link, index) => {
                const Icon = link.icon;
                return (
                  <Link key={index} to={link.link}>
                    <Card className="p-6 h-full hover:shadow-xl transition-all duration-300 group border-gray-100 bg-white cursor-pointer">
                      <div className="flex items-start space-x-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-teal-500 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                          <Icon className="h-6 w-6 text-white" />
                        </div>
                        <div>
                          <h3 className="text-lg font-bold text-gray-900 mb-1 group-hover:text-blue-600 transition-colors">
                            {link.title}
                          </h3>
                          <p className="text-gray-600 text-sm">
                            {link.description}
                          </p>
                        </div>
                      </div>
                    </Card>
                  </Link>
                );
              })}
            </div>
          </div>

          <div className="mb-20">
            <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">Popular Articles</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {popularArticles.map((article, index) => {
                const Icon = article.icon;
                return (
                  <Card key={index} className="p-6 hover:shadow-xl transition-all duration-300 group border-gray-100 bg-white cursor-pointer">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-blue-200 transition-colors">
                        <Icon className="h-6 w-6 text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                          {article.title}
                        </h3>
                        <p className="text-gray-600 text-sm">
                          {article.description}
                        </p>
                      </div>
                      <ArrowRight className="h-5 w-5 text-gray-400 group-hover:text-blue-600 group-hover:translate-x-1 transition-all" />
                    </div>
                  </Card>
                );
              })}
            </div>
          </div>

          <div className="max-w-3xl mx-auto">
            <Card className="p-8 bg-gradient-to-br from-blue-600 to-teal-500 border-0 shadow-2xl">
              <div className="text-center text-white">
                <Phone className="h-12 w-12 mx-auto mb-4 opacity-90" />
                <h2 className="text-2xl font-bold mb-2">Still need help?</h2>
                <p className="text-blue-100 mb-6">
                  Our support team is available to assist you with any questions
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                  <a 
                    href="tel:9455545273" 
                    className="inline-flex items-center gap-2 bg-white text-blue-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
                  >
                    <Phone className="h-5 w-5" />
                    94555 45273
                  </a>
                  <Link to="/contact-us">
                    <Button className="bg-white/20 text-white border-2 border-white hover:bg-white/30 px-6 py-3">
                      Contact Us
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </Link>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HelpCenter;
